import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Category {
  name: string;
  url: string;
  icon: string;
}

const categories: Category[] = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
  { name: 'Hoodies / Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', icon: 'ri-heart-line' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html', icon: 'ri-t-shirt-line' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html', icon: 'ri-t-shirt-air-line' },
  { name: 'Pants / Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html', icon: 'ri-pantone-line' },
  { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html', icon: 'ri-base-station-line' },
  { name: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html', icon: 'ri-stack-line' },
  { name: 'Underwear / Underpants', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html', icon: 'ri-checkbox-blank-line' },
  { name: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html', icon: 'ri-shirt-line' },
  { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html', icon: 'ri-vip-crown-line' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', icon: 'ri-shopping-bag-3-line' },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html', icon: 'ri-loop-left-line' },
  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html', icon: 'ri-drop-line' },
  { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html', icon: 'ri-more-line' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* JSON-LD Structured Data for WebSite */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Kakobuy Spreadsheet Hub",
          "url": import.meta.env.VITE_SITE_URL || "https://kakobuyfinds.boats",
          "description": "Discover the best Kakobuy Spreadsheet with organized product finds, shopping guides, category comparisons, and updated resources.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": (import.meta.env.VITE_SITE_URL || "https://kakobuyfinds.boats") + "?s={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(0,0,0,0.06)]' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground-950 hover:text-primary-500 transition-colors duration-200"
            >
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-database-2-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading font-bold text-lg md:text-xl whitespace-nowrap">
                Kakobuy Hub
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3">
              {/* Category Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-foreground-300 bg-background-50 text-foreground-800 text-sm font-medium hover:border-foreground-500 hover:text-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <span>Select Category</span>
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 border border-background-200 rounded-xl py-2 shadow-[0_4px_24px_rgba(0,0,0,0.08)] max-h-80 overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                        onClick={() => setIsOpen(false)}
                      >
                        <i className={`${cat.icon} text-foreground-400 text-sm`}></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* CTA Buttons */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                kakobuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2.5 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                buy it now
              </a>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Enhanced Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-background-200/60 py-4 px-1 max-h-[calc(100vh-4rem)] overflow-y-auto animate-[fadeIn_0.25s_ease-out]">
              {/* Mobile CTAs - Prominent at top */}
              <div className="flex gap-2 mb-5 px-1">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex-1 text-center px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  kakobuy sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex-1 text-center px-4 py-3 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  buy it now
                </a>
              </div>

              {/* Mobile Category Selector - 2 column grid with icons */}
              <div>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-1 h-4 rounded-full bg-primary-500"></div>
                  <p className="text-xs font-bold text-foreground-500 uppercase tracking-widest">
                    Browse Categories
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm text-foreground-700 bg-background-50 border border-background-200/60 hover:bg-background-100 hover:border-primary-200/60 hover:text-foreground-950 transition-all duration-150 whitespace-nowrap active:scale-[0.98] cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      <span className="text-xs font-medium truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div className="mt-4 pt-4 border-t border-background-200/60">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-1 h-4 rounded-full bg-accent-500"></div>
                  <p className="text-xs font-bold text-foreground-500 uppercase tracking-widest">
                    Guides & Resources
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-1">
                  <Link
                    to="/kakobuy-spreadsheet-ultimate-guide-2026"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-article-line text-foreground-400 text-sm"></i>
                    <span>Ultimate Guide 2026</span>
                  </Link>
                  <Link
                    to="/kakobuy-vs-cssbuy-vs-superbuy-comparison"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-scales-3-line text-foreground-400 text-sm"></i>
                    <span>Platform Comparison</span>
                  </Link>
                  <Link
                    to="/kakobuy-spreadsheet-safety-buying-guide"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-shield-check-line text-foreground-400 text-sm"></i>
                    <span>Safety Guide</span>
                  </Link>
                  <Link
                    to="/kakobuy-spreadsheet-beginner-guide"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-user-star-line text-foreground-400 text-sm"></i>
                    <span>Beginner Guide</span>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </nav>
    </>
  );
}